import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Pagination, Response } from '@ui/src';
import { RateItem } from '../../types';
import { exchangeRatesApi } from '../services';

export interface RateSidepanelState {
  rate?: RateItem;
  panelIsOpen: boolean;
  rateHistory?: RateItem[];
  pagination?: Pagination;
  expandedRateHistoryId?: string;
  errorsList?: string;
}

const initialState: RateSidepanelState = {
  panelIsOpen: false,
};

export const rateSidepanelSlice = createSlice({
  name: 'rateSidepanel',
  initialState,
  reducers: {
    setRateHistory: (
      state,
      action: PayloadAction<{
        edges: { node: RateItem }[];
        pageInfo: Pagination;
      }>,
    ) => {
      state.rateHistory = action.payload.edges?.map((item) => item.node);
      state.pagination = action.payload.pageInfo;
    },
    setExpandedRateHistoryId: (state, action: PayloadAction<string>) => {
      state.expandedRateHistoryId = action.payload;
    },
    changePanelState: (state, action: PayloadAction<boolean>) => {
      state.panelIsOpen = action.payload;
    },
    clearRateSidepanelState: (state) => {
      state.rate = undefined;
      state.panelIsOpen = false;
      state.rateHistory = undefined;
      state.expandedRateHistoryId = undefined;
      state.errorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      exchangeRatesApi.endpoints.getRate.matchFulfilled,
      (state, action: PayloadAction<{ data: RateItem }>) => {
        state.rate = action.payload.data;
      },
    );
    builder.addMatcher(
      exchangeRatesApi.endpoints.updateRate.matchFulfilled,
      (state, action: PayloadAction<{ data: RateItem }>) => {
        state.rate = action.payload.data;
      },
    );
    builder.addMatcher(
      exchangeRatesApi.endpoints.getExchangePairRates.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      exchangeRatesApi.endpoints.getExchangePairRates.matchRejected,
      (state, action: any) => {
        state.rateHistory = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      exchangeRatesApi.endpoints.getExchangePairRates.matchFulfilled,
      (state, action: PayloadAction<Response<RateItem>>) => {
        state.rateHistory = action.payload.data.edges.map((item) => item.node);
        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      exchangeRatesApi.endpoints.getRateHistoryDetails.matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: RateItem }> & {
          meta: {
            arg: {
              originalArgs: string;
            };
          };
        },
      ) => {
        if (state.rateHistory) {
          const nextRateHistory = state.rateHistory?.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs) {
              result.logs = action.payload.data.logs;
            }

            return result;
          });

          state.rateHistory = nextRateHistory;
        }
      },
    );
  },
});

export const {
  changePanelState,
  clearRateSidepanelState,
  setRateHistory,
  setExpandedRateHistoryId,
} = rateSidepanelSlice.actions;

export default rateSidepanelSlice.reducer;
